<template>
  <div
    :class="[
      showBorder ? 'bg-blue-50 dark:bg-blue-900/30 p-3 rounded-md border border-blue-200 dark:border-blue-800' : ''
    ]"
  >
    <h3
      v-if="title"
      class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2"
    >
      {{ title }}
    </h3>
    <LoadingSpinner
      v-if="isLoading"
      size="sm"
      color="primary"
      :text="t('app.views.cashRegister.loading')"
    />
    <div
      v-else-if="cutData"
      class="space-y-2"
    >
      <div class="grid grid-cols-2 gap-3">
        <div>
          <p class="text-xl font-bold text-blue-700 dark:text-blue-300">
            {{ formatCurrency(cutData.net_cash_flow || 0) }}
          </p>
          <p class="text-xs text-gray-600 dark:text-gray-400">
            {{ formatDate(cutData.generated_at || cutData.created_at) }}
          </p>
        </div>
        <div class="text-right">
          <p class="text-sm text-gray-700 dark:text-gray-300">
            {{ t('app.views.cashRegister.sessionNumber') }} #{{ cutData.session_id }}
          </p>
          <p class="text-xs text-gray-600 dark:text-gray-400">
            {{ cutData.total_transactions || 0 }} {{ t('app.views.cashRegister.transactions') }}
          </p>
        </div>
      </div>

      <!-- Payment Breakdown -->
      <div class="border-t border-blue-200 dark:border-blue-800 pt-2">
        <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
          {{ t('app.views.cashRegister.paymentBreakdown') }}
        </h4>
        <div class="flex flex-col text-sm space-y-0.5">
          <template
            v-for="(amount, method) in allPaymentMethods"
            :key="method"
          >
            <div
              v-if="amount > 0"
              class="flex justify-between"
            >
              <span class="text-gray-600 dark:text-gray-400">{{ getPaymentMethodLabel(method) }}:</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatCurrency(amount) }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- Transaction Summary -->
      <div class="border-t border-blue-200 dark:border-blue-800 pt-2">
        <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
          {{ t('app.views.cashRegister.transactionSummary') }}
        </h4>
        <div class="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div class="rounded-md bg-blue-100/70 px-3 py-2 dark:bg-blue-900/20">
            <p class="text-gray-600 dark:text-gray-400">
              {{ t('app.views.cashRegister.totalSales') }}:
            </p>
            <p class="text-base font-semibold text-green-700 dark:text-green-300">
              {{ formatCurrency(totalSales) }}
            </p>
          </div>
          <div class="rounded-md bg-blue-100/70 px-3 py-2 dark:bg-blue-900/20">
            <p class="text-gray-600 dark:text-gray-400">
              {{ t('app.views.cashRegister.totalExpenses') }}:
            </p>
            <p class="text-base font-semibold text-red-600 dark:text-red-400">
              {{ formatCurrency(totalExpenses) }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <p
      v-else
      class="text-gray-600 dark:text-gray-400"
    >
      {{ emptyMessage || t('app.views.cashRegister.noLastCut') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { formatCurrency } from '@/utils/priceHelpers'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
}

interface Props {
  cutData?: any
  isLoading?: boolean
  title?: string
  emptyMessage?: string
  showBorder?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  cutData: null,
  isLoading: false,
  title: '',
  emptyMessage: '',
  showBorder: true
})

const { t } = useI18n()
const authStore = useAuthStore()

const parseAmount = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

const totalSales = computed(() => parseAmount(props.cutData?.total_sales ?? props.cutData?.sales_total))
const totalExpenses = computed(() => parseAmount(props.cutData?.total_expenses ?? props.cutData?.expenses_total))

// Get all payment methods with their amounts, including custom methods
const allPaymentMethods = computed(() => {
  const breakdown = props.cutData?.payment_breakdown || {}
  const result: Record<string, number> = {}
  
  // Add standard methods
  result.cash = parseAmount(breakdown.cash || props.cutData?.cash_payments || 0)
  result.card = parseAmount(breakdown.card || props.cutData?.card_payments || 0)
  result.digital = parseAmount(breakdown.digital || 0)
  result.other = parseAmount(breakdown.other || 0)
  
  // Add custom methods from restaurant config
  const customMethods = authStore.restaurant?.payment_methods_config?.custom_methods || []
  customMethods.forEach((method: any) => {
    if (method.enabled) {
      result[method.name] = parseAmount(breakdown[method.name] || 0)
    }
  })
  
  return result
})

// Get label for payment method
const getPaymentMethodLabel = (method: string): string => {
  const methodMap: Record<string, string> = {
    'cash': t('app.views.cashRegister.cash') || 'Cash',
    'card': t('app.views.cashRegister.card') || 'Card',
    'digital': t('app.views.cashRegister.digital') || 'Digital',
    'other': t('app.views.cashRegister.other') || 'Other'
  }
  return methodMap[method.toLowerCase()] || method
}
</script>
